import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-jalaali';

interface PersianDatePickerProps {
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const KNOWN_INPUT_FORMATS = [
  'jYYYY/jMM/jDD HH:mm',
  'jYYYY/jMM/jDD',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY/MM/DD HH:mm',
  'YYYY/MM/DD',
];

const parseValue = (value?: string | null) => {
  if (typeof value !== 'string') {
    return moment();
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return moment();
  }

  const parsed = moment(trimmed, KNOWN_INPUT_FORMATS, true);
  if (parsed && typeof parsed.isValid === 'function' && parsed.isValid()) {
    return parsed;
  }

  const fallback = moment(trimmed);
  if (fallback && typeof fallback.isValid === 'function' && fallback.isValid()) {
    return fallback;
  }
  return moment();
};

const PersianDatePicker: React.FC<PersianDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'تاریخ را انتخاب کنید',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => parseValue(value));

  useEffect(() => {
    setSelectedDate(parseValue(value));
  }, [value]);

  const currentValueMoment = useMemo(() => {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    if (!normalized) return null;

    // If value is UTC ISO string, parse it as UTC
    if (normalized.includes('T') && (normalized.includes('Z') || normalized.includes('+'))) {
      const utc = moment.utc(normalized);
      return utc && typeof utc.isValid === 'function' && utc.isValid() ? utc : null;
    }

    // If value is local datetime string, parse it as local
    if (normalized.includes('T') && normalized.length === 19 && !normalized.includes('Z') && !normalized.includes('+')) {
      const local = moment(normalized);
      return local && typeof local.isValid === 'function' && local.isValid() ? local : null;
    }

    const parsed = parseValue(normalized);
    return parsed && typeof parsed.isValid === 'function' && parsed.isValid() ? parsed : null;
  }, [value]);

  const displayValue = currentValueMoment
    ? currentValueMoment.format('jYYYY/jMM/jDD - HH:mm')
    : '';

  const handleDateSelect = (date: moment.Moment) => {
    setSelectedDate(date);

    // ذخیره تاریخ به صورت local date string برای جلوگیری از مشکل timezone
    const localDateString = date.format('YYYY-MM-DDTHH:mm:ss');
    onChange(localDateString);
    setIsOpen(false);
  };

  const generateCalendarDays = () => {
    const startOfMonth = selectedDate.clone().startOf('jMonth');
    const endOfMonth = selectedDate.clone().endOf('jMonth');
    const startOfCalendar = startOfMonth.clone().startOf('week');
    const endOfCalendar = endOfMonth.clone().endOf('week');

    const days = [];
    const day = startOfCalendar.clone();

    while (day.isSameOrBefore(endOfCalendar, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  };

  const monthNames = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
  ];

  const dayNames = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={displayValue}
        onClick={() => setIsOpen(!isOpen)}
        placeholder={placeholder}
        readOnly
        className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition-all focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
      />
      
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_50px_-24px_rgba(15,23,42,0.35)]">
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 p-3">
            <button
              onClick={() => setSelectedDate(selectedDate.clone().subtract(1, 'jMonth'))}
              className="rounded-lg p-1.5 text-slate-600 transition-colors hover:bg-white hover:text-indigo-700"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="text-center">
              <div className="font-semibold">
                {monthNames[selectedDate.jMonth()]} {selectedDate.jYear()}
              </div>
            </div>
            
            <button
              onClick={() => setSelectedDate(selectedDate.clone().add(1, 'jMonth'))}
              className="rounded-lg p-1.5 text-slate-600 transition-colors hover:bg-white hover:text-indigo-700"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 px-2 pt-2">
            {dayNames.map((dayName) => (
              <div key={dayName} className="py-1.5 text-center text-xs font-semibold text-slate-500">
                {dayName}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 px-2 pb-2">
            {generateCalendarDays().map((day, index) => {
              const isCurrentMonth = day.jMonth() === selectedDate.jMonth();
              const isToday = day.isSame(moment(), 'day');
              const isSelected = !!currentValueMoment && day.isSame(currentValueMoment, 'day');

              return (
                <button
                  key={index}
                  onClick={() => handleDateSelect(day)}
                  type="button"
                  className={`
                    rounded-lg p-2 text-sm transition
                    ${isCurrentMonth ? 'text-slate-800' : 'text-slate-300'}
                    ${isToday ? 'bg-indigo-100 font-semibold text-indigo-700' : ''}
                    ${isSelected ? 'bg-indigo-600 font-bold text-white hover:bg-indigo-600' : 'hover:bg-slate-100'}
                  `}
                >
                  {day.jDate()}
                </button>
              );
            })}
          </div>

          <div className="border-t border-slate-100 p-3">
            <div className="flex items-center space-x-2 space-x-reverse">
              <label className="text-sm text-slate-600">ساعت:</label>
              <input
                type="time"
                value={currentValueMoment ? currentValueMoment.format('HH:mm') : selectedDate.format('HH:mm')}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':');
                  const baseDate = currentValueMoment || selectedDate;
                  const newDate = baseDate.clone().hours(parseInt(hours)).minutes(parseInt(minutes));
                  setSelectedDate(newDate);
                  // ذخیره به صورت local date string برای جلوگیری از مشکل timezone
                  const localDateString = newDate.format('YYYY-MM-DDTHH:mm:ss');
                  onChange(localDateString);
                }}
                className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                پاک‌کردن
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-indigo-700"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersianDatePicker;
